/**
 * Utility functions for the Car-IA app
 */

// ============================================================
// Date/Time formatting
// ============================================================

export function formatDate(date: string | Date, options?: Intl.DateTimeFormatOptions): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    ...options,
  });
}

export function formatDateTime(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatRelativeTime(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Agora mesmo';
  if (diffMins < 60) return `${diffMins}min atrás`;
  if (diffHours < 24) return `${diffHours}h atrás`;
  if (diffDays < 7) return `${diffDays}d atrás`;
  return formatDate(d);
}

// ============================================================
// Status formatting
// ============================================================

export type InspectionStatus = 'processing' | 'done' | 'failed';

export function getStatusLabel(status: InspectionStatus): string {
  const labels: Record<InspectionStatus, string> = {
    processing: 'Processando',
    done: 'Concluída',
    failed: 'Falhou',
  };
  return labels[status];
}

export function getStatusChipVariant(status: InspectionStatus): 'processing' | 'success' | 'error' {
  const variants: Record<InspectionStatus, 'processing' | 'success' | 'error'> = {
    processing: 'processing',
    done: 'success',
    failed: 'error',
  };
  return variants[status];
}

export function getGradeLabel(grade: 'A' | 'B' | 'C'): string {
  const labels = { A: 'Excelente', B: 'Bom', C: 'Atenção' };
  return labels[grade];
}

export function getGradeColor(grade: 'A' | 'B' | 'C'): string {
  const colors = { A: 'success', B: 'warning', C: 'error' as const };
  return colors[grade];
}

// ============================================================
// File/Size formatting
// ============================================================

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

export function formatNumber(num: number): string {
  return new Intl.NumberFormat('pt-BR').format(num);
}

// ============================================================
// Validation
// ============================================================

export function validatePlate(plate: string): { valid: boolean; formatted?: string; error?: string } {
  const clean = plate.toUpperCase().replace(/[^A-Z0-9]/g, '');
  
  if (clean.length === 0) {
    return { valid: false, error: 'Placa é obrigatória' };
  }
  
  if (clean.length !== 7) {
    return { valid: false, error: 'Placa deve ter 7 caracteres' };
  }
  
  // Mercosul: AAA0A00
  const mercosulRegex = /^[A-Z]{3}[0-9][A-Z][0-9]{2}$/;
  // Old: AAA0000
  const oldRegex = /^[A-Z]{3}[0-9]{4}$/;
  
  if (!mercosulRegex.test(clean) && !oldRegex.test(clean)) {
    return { valid: false, error: 'Formato de placa inválido' };
  }
  
  // Format for display
  let formatted: string;
  if (mercosulRegex.test(clean)) {
    formatted = `${clean.slice(0, 3)}${clean.slice(3, 4)}${clean.slice(4)}`;
  } else {
    formatted = `${clean.slice(0, 3)}-${clean.slice(3)}`;
  }
  
  return { valid: true, formatted };
}

// ============================================================
// Image utilities
// ============================================================

export function createImagePreview(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(url);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Falha ao carregar imagem'));
    };
    img.src = url;
  });
}

export function compressImage(
  file: File,
  options: { maxWidth?: number; maxHeight?: number; quality?: number } = {}
): Promise<File> {
  const { maxWidth = 1920, maxHeight = 1920, quality = 0.85 } = options;
  
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    
    img.onload = () => {
      URL.revokeObjectURL(url);
      
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas não suportado'));
        return;
      }
      
      let { width, height } = img;
      
      // Calculate new dimensions
      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width *= ratio;
        height *= ratio;
      }
      
      canvas.width = width;
      canvas.height = height;
      
      // Draw and compress
      ctx.drawImage(img, 0, 0, width, height);
      
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const compressedFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });
            resolve(compressedFile);
          } else {
            reject(new Error('Falha na compressão'));
          }
        },
        'image/jpeg',
        quality
      );
    };
    
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Falha ao carregar imagem para compressão'));
    };
    
    img.src = url;
  });
}

// ============================================================
// Debounce
// ============================================================

export function debounce<T extends (...args: unknown[]) => unknown>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: number | null = null;
  
  return (...args: Parameters<T>) => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = window.setTimeout(() => fn(...args), delay);
  };
}

// ============================================================
// URL utilities
// ============================================================

export function getUrlParams(): Record<string, string> {
  const params = new URLSearchParams(window.location.search);
  const result: Record<string, string> = {};
  params.forEach((value, key) => {
    result[key] = value;
  });
  return result;
}

export function buildUrl(path: string, params: Record<string, string | number | boolean | undefined>): string {
  const url = new URL(path, window.location.origin);
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      url.searchParams.set(key, String(value));
    }
  });
  return url.toString();
}